// MobileBestOnDesktopBanner — a brief one-time welcome shown to mobile
// players on the main menu. V5 mobile UX pass: replaced the original
// "Best on desktop" copy with a positive, actionable onboarding message
// (the touch control scheme is actually solid: tap to select / move /
// attack, two-finger pan+zoom, persistent action bar). Telling a portal
// player the game wasn't built for them is the wrong first impression.
//
// Dismissal is persisted in localStorage so a returning player on the
// same device doesn't see it again. Touch-profile detection is the
// same heuristic InputRouter / OrientationHint use.

import { injectHudThemeStyles } from './hudThemeStyles.js';

const ROOT_ID = 'mobile-best-on-desktop-banner';
const STYLE_ID = 'mobile-best-on-desktop-styles';
const STORAGE_KEY = 'signalclash.mobileBanner.dismissed';

export class MobileBestOnDesktopBanner {
  private root: HTMLDivElement | null = null;

  showIfMobile(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (this.alreadyDismissed()) return;
    if (!this.isMobile()) return;
    injectStyles();
    this.mount();
  }

  hide(): void {
    if (this.root === null) return;
    this.root.remove();
    this.root = null;
  }

  private alreadyDismissed(): boolean {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }

  private markDismissed(): void {
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // localStorage unavailable — banner shows again, no functional harm.
    }
  }

  private isMobile(): boolean {
    if (typeof window === 'undefined') return false;
    const coarse = window.matchMedia?.('(pointer: coarse)').matches === true;
    const narrow = window.innerWidth < 820;
    return coarse || narrow;
  }

  private mount(): void {
    if (this.root !== null) return;
    const div = document.createElement('div');
    div.id = ROOT_ID;
    div.innerHTML = `
      <div class="mobile-best-banner__title">Touch-ready</div>
      <div class="mobile-best-banner__body">
        Tap to select and move. Two-finger pinch to zoom.
        Use the action bar for Build and abilities. Landscape gives the best view.
      </div>
      <button class="mobile-best-banner__close" type="button">Let's play</button>
    `;
    document.body.appendChild(div);
    this.root = div;
    const btn = div.querySelector('button');
    btn?.addEventListener('click', () => {
      this.markDismissed();
      this.hide();
    });
  }
}

function injectStyles(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
#${ROOT_ID} {
  position: fixed;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  z-index: 40;
  width: min(90vw, 360px);
  padding: 14px 18px;
  border-radius: 8px;
  background: rgba(12, 14, 18, 0.96);
  border: 1px solid rgba(160, 200, 255, 0.32);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.55);
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  color: #d4dae2;
  text-align: center;
  animation: mobile-best-in 320ms ease-out;
}
.mobile-best-banner__title {
  font-size: 14px;
  font-weight: 700;
  color: #a9c8ff;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 6px;
}
.mobile-best-banner__body {
  font-size: 12px;
  color: #aeb6c0;
  line-height: 1.5;
  margin-bottom: 12px;
}
.mobile-best-banner__close {
  font: inherit;
  font-size: 12px;
  padding: 7px 14px;
  border-radius: 4px;
  background: #2a3340;
  color: #eef3f8;
  border: 1px solid rgba(160, 200, 255, 0.3);
  cursor: pointer;
}
.mobile-best-banner__close:hover { background: #3a4555; }
@keyframes mobile-best-in {
  0%   { opacity: 0; transform: translate(-50%, 12px); }
  100% { opacity: 1; transform: translate(-50%, 0); }
}
  `;
  document.head.appendChild(style);
  injectHudThemeStyles();
}
